const BUNDLE_UPDATED_EVENT = "BUNDLE_UPDATED";

function startTransition(callback) {
  const unsupported = () => {
    const resolvedPromise = Promise.resolve(callback());
    return {
      ready: resolvedPromise,
      updateCallbackDone: resolvedPromise,
      finished: resolvedPromise,
      skipTransition: () => {},
    };
  };

  if (!document.startViewTransition) return unsupported();

  try {
    return document.startViewTransition(callback);
  } catch {
    return unsupported();
  }
}

class BundleUpdated extends CustomEvent {
  constructor(items, totalSubscriptionPrice, totalOneTimePrice, totalOneTimeDiscountPrice) {
    super(BUNDLE_UPDATED_EVENT);
    this.items = items;
    this.totalSubscriptionPrice = totalSubscriptionPrice;
    this.totalOneTimePrice = totalOneTimePrice;
    this.totalOneTimeDiscountPrice = totalOneTimeDiscountPrice;
  }
}

const EMPTY_BUNDLE_SUMMARY_ITEM = document.querySelector("#bundle-builder-summary-item-empty")?.content;
const BUNDLE_SUMMARY_ITEM = document.querySelector("#bundle-builder-summary-item")?.content;
const BUNDLE_CTA_MOBILE = document.querySelector("#bundle-builder-cta-mobile")?.content;

if (!customElements.get("bundle-builder-context")) {
  customElements.define(
    "bundle-builder-context",
    class extends HTMLElement {
      bundleItems = [];
      numBundleItems = 0;
      parentItemVariantId;
      minItems;
      maxItems;
      defaultToSubscriptionPrice = true;
      totalSubscriptionPrice = 0.0;
      totalOneTimePrice = 0.0;
      totalOneTimeDiscountPrice = 0.0;
      isEditingBundle = false;

      connectedCallback() {
        const defaultToSubscriptionPrice = this.getAttribute("default-to-subscription-price");
        if (!defaultToSubscriptionPrice) {
          throw new Error(
            "Element `bundle-builder-context` requires a `default-to-subscription-price` attribute"
          );
        }

        const parentItemVariantId = this.getAttribute("parent-item-variant-id");
        if (!parentItemVariantId) {
          throw new Error(
            "Element `bundle-builder-context` requires a `parent-item-variant-id` attribute"
          );
        }

        const minItems = this.getAttribute("min-items");
        if (!minItems) {
          throw new Error("Element `bundle-builder-context` requires a `min-items` attribute");
        }

        const maxItems = this.getAttribute("max-items");
        if (!maxItems) {
          throw new Error("Element `bundle-builder-context` requires a `max-items` attribute");
        }

        this.minItems = parseInt(minItems, 10);
        this.maxItems = parseInt(maxItems, 10);
        this.parentItemVariantId = parseInt(parentItemVariantId, 10);
        this.defaultToSubscriptionPrice = defaultToSubscriptionPrice.toLowerCase() === "true";

        setTimeout(() => this.loadState(), 1);

        this._onRestoreRequested = () => this.loadState();
        this.addEventListener("BUNDLE_RESTORE_REQUESTED", this._onRestoreRequested);
      }

      disconnectedCallback() {
        if (this._onRestoreRequested) {
          this.removeEventListener("BUNDLE_RESTORE_REQUESTED", this._onRestoreRequested);
        }
      }

      loadState() {
        if (this.loadStateFromUrl()) return;

        const saved = localStorage.getItem("bundle_builder_items");
        if (saved) {
          this.bundleItems = JSON.parse(saved);
          this.isEditingBundle = !!localStorage.getItem("editing_bundle_id");
          if (this.isEditingBundle) this.setAttribute("edit-mode", "true");
          this.recalculatePricesAndTriggerEvent();
        }
      }

      loadStateFromUrl() {
        const url = new URL(window.location.href);
        if (url.searchParams.has("prebuilt-bundle")) {
          const id = url.searchParams.get("prebuilt-bundle");
          const scriptNode = document.querySelector("#bundle-builder-prebuilt-options");
          if (!scriptNode) return false;

          const scriptContents = JSON.parse(scriptNode.innerText);
          const match = scriptContents.find((b) => b.id === id);
          if (!match) return false;

          for (const item of match.variants) this.addItem(item);

          const subInput = document.querySelector('input[name="subscription"]');
          if (subInput) subInput.checked = true;

          url.searchParams.delete("prebuilt-bundle");
          history.replaceState({}, "", url.toString());
          return true;
        }
        return false;
      }

      addItem({
        variantId,
        quantity,
        subscriptionPrice,
        oneTimePrice,
        imageUrl,
        title,
        variantTitle,
        oneTimeDiscountPrice,
      }) {
        this.bundleItems.push({
          variantId: parseInt(variantId, 10),
          quantity: parseInt(quantity, 10),
          subscriptionPrice: parseFloat(subscriptionPrice),
          oneTimePrice: parseFloat(oneTimePrice),
          imageUrl,
          title,
          variantTitle,
          oneTimeDiscountPrice: parseFloat(oneTimeDiscountPrice),
        });

        this.recalculatePricesAndTriggerEvent();
      }

      updateItem({ variantId, quantity }) {
        const vid = parseInt(variantId, 10);
        const qty = parseInt(quantity, 10);

        const index = this.bundleItems.findIndex((item) => item.variantId === vid);
        if (index === -1) return;

        if (qty === 0) {
          this.bundleItems.splice(index, 1);
        } else {
          this.bundleItems[index].quantity = qty;
        }

        this.recalculatePricesAndTriggerEvent();
      }

      recalculatePricesAndTriggerEvent() {
        this.numBundleItems = this.bundleItems.reduce((acc, item) => acc + item.quantity, 0);

        this.totalOneTimePrice = this.bundleItems.reduce(
          (acc, item) => acc + item.oneTimePrice * item.quantity,
          0.0
        );

        this.totalSubscriptionPrice = this.bundleItems.reduce(
          (acc, item) => acc + item.subscriptionPrice * item.quantity,
          0.0
        );

        this.totalOneTimeDiscountPrice = this.bundleItems.reduce(
          (acc, item) => acc + item.oneTimeDiscountPrice * item.quantity,
          0.0
        );

        this.setAttribute("full", this.numBundleItems === this.maxItems ? "true" : "false");

        localStorage.setItem("bundle_builder_items", JSON.stringify(this.bundleItems));

        this.dispatchEvent(
          new BundleUpdated(
            this.bundleItems,
            this.totalSubscriptionPrice,
            this.totalOneTimePrice,
            this.totalOneTimeDiscountPrice
          )
        );
      }

      addToCart(sellingPlanId = undefined) {
        const guid = crypto.randomUUID();
        const cartItems = [
          {
            id: this.parentItemVariantId,
            quantity: 1,
            selling_plan: sellingPlanId,
            properties: {
              _bundle_id: guid,
              _bundle_parent: "true",
              ["Selected Products"]: this.bundleItems.map((i) => i.title).join(" / "),
            },
          },
          ...this.bundleItems.map((item) => ({
            id: item.variantId,
            quantity: item.quantity,
            selling_plan: sellingPlanId,
            properties: { _bundle_id: guid },
          })),
        ];

        fetch(Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: cartItems, sections: ["mini-cart"] }),
        }).then(() =>
          fetch(Shopify.routes.root + "cart.js").then((response) =>
            response.json().then((json) => {
              const dialog = document.getElementById("select-delivery");
              if (dialog) dialog.open = false;

              setTimeout(async () => {
                const updatedCartContent = await freeGiftHandler(json);

                document.documentElement.dispatchEvent(
                  new CustomEvent("cart:updated", {
                    bubbles: true,
                    detail: { cart: updatedCartContent },
                  })
                );

                updatedCartContent["sections"] = json["sections"];

                document.documentElement.dispatchEvent(
                  new CustomEvent("cart:refresh", {
                    bubbles: true,
                    detail: {
                      cart: updatedCartContent,
                      openMiniCart:
                        window.themeVariables.settings.cartType === "drawer" &&
                        this.closest(".drawer") === null,
                    },
                  })
                );
              }, 250);

              localStorage.removeItem("bundle_builder_items");
              localStorage.removeItem("editing_bundle_id");
              this.setAttribute("edit-mode", "false");

              this.bundleItems = [];
              this.recalculatePricesAndTriggerEvent();
            })
          )
        );
      }
    }
  );
}

if (!customElements.get("bundle-builder-product-card")) {
  customElements.define(
    "bundle-builder-product-card",
    class extends HTMLElement {
      connectedCallback() {
        const variantId = this.getAttribute("variant-id");
        if (!variantId) {
          throw new Error("Element `bundle-builder-product-card` requires a `variant-id` attribute");
        }

        const oneTimePrice = this.getAttribute("onetime-price");
        if (!oneTimePrice) {
          throw new Error("Element `bundle-builder-product-card` requires a `onetime-price` attribute");
        }

        const oneTimeDiscountPrice = this.getAttribute("onetime-discount-price");
        if (!oneTimeDiscountPrice) {
          throw new Error(
            "Element `bundle-builder-product-card` requires a `onetime-discount-price` attribute"
          );
        }

        const subscriptionPrice = this.getAttribute("subscription-price");
        if (!subscriptionPrice) {
          throw new Error(
            "Element `bundle-builder-product-card` requires a `subscription-price` attribute"
          );
        }

        const productTitle = this.getAttribute("product-title");
        if (!productTitle) {
          throw new Error("Element `bundle-builder-product-card` requires a `product-title` attribute");
        }

        const bundleContext = this.closest("bundle-builder-context");
        if (!bundleContext) {
          throw new Error(
            "Element `bundle-builder-product-card` must be a child of `bundle-builder-context`"
          );
        }

        this.variantId = parseInt(variantId, 10);
        this.oneTimePrice = parseFloat(oneTimePrice);
        this.oneTimeDiscountPrice = parseFloat(oneTimeDiscountPrice);
        this.subscriptionPrice = parseFloat(subscriptionPrice);
        this.bundleContext = bundleContext;
        this.productTitle = productTitle;
        this.variantTitle = this.getAttribute("variant-title");

        this.addButton = this.querySelector(".add-bundle-button");
        this.input = this.querySelector(".quantity-selector__input");
        if (!this.addButton || !this.input) return;

        this._onBundleUpdated = () => this.updateInitialView();
        this.bundleContext.addEventListener(BUNDLE_UPDATED_EVENT, this._onBundleUpdated);

        this._onAddClick = () => this.handleAddToBundle();
        this.addButton.addEventListener("click", this._onAddClick);

        this._onQtyChanged = () => this.handleQuantityChange();
        this.input.addEventListener("input:quantity-changed", this._onQtyChanged);

        this.updateInitialView();
      }

      disconnectedCallback() {
        if (this.addButton && this._onAddClick) {
          this.addButton.removeEventListener("click", this._onAddClick);
        }
        if (this.input && this._onQtyChanged) {
          this.input.removeEventListener("input:quantity-changed", this._onQtyChanged);
        }
        if (this.bundleContext && this._onBundleUpdated) {
          this.bundleContext.removeEventListener(BUNDLE_UPDATED_EVENT, this._onBundleUpdated);
        }
      }

      updateInitialView() {
        requestAnimationFrame(() => {
          if (!this.input || !this.bundleContext) return;

          //this.input.max = this.bundleContext.maxItems - this.bundleContext.numBundleItems;
          //this.addButton.disabled = this.input.max === 0;

          const match = this.bundleContext.bundleItems.find((it) => it.variantId === this.variantId);
          if (!match) {
            const qs = this.querySelector("quantity-selector");
            if (qs) qs.style.display = "none";
            this.addButton.style.removeProperty("display");
            return;
          }

          this.input.setQuantitySilently(match.quantity);

          this.addButton.style.display = "none";
          const qs = this.querySelector("quantity-selector");
          if (qs) qs.style.removeProperty("display");
        });
      }

      handleQuantityChange() {
        if (!this.input || !this.bundleContext) return;
        const newQuantity = parseInt(this.input.quantity, 10);

        this.bundleContext.updateItem({
          variantId: this.variantId,
          quantity: newQuantity,
        });
      }

      handleAddToBundle() {
        this.bundleContext.addItem({
          variantId: this.variantId,
          quantity: 1,
          subscriptionPrice: this.subscriptionPrice,
          oneTimePrice: this.oneTimePrice,
          oneTimeDiscountPrice: this.oneTimeDiscountPrice,
          title: this.productTitle,
          imageUrl: this.querySelector("img")?.currentSrc,
          variantTitle: this.variantTitle,
        });

        this.input?.setQuantitySilently(1);
      }
    }
  );
}

if (!customElements.get("bundle-builder-summary")) {
  customElements.define(
    "bundle-builder-summary",
    class extends HTMLElement {
      connectedCallback() {
        const bundleContext = this.closest("bundle-builder-context");
        if (!bundleContext) {
          throw new Error("Element `bundle-builder-summary` must be a child of `bundle-builder-context`");
        }

        const button = this.querySelector(".bundle-builder-continue-button");
        if (!button) {
          throw new Error(
            "Element `bundle-builder-summary` must be a child of `.bundle-builder-continue-button`"
          );
        }

        this.bundleContext = bundleContext;
        this.button = button;

        this._onBundleUpdated = () => this.handleBundleEvent();
        this.bundleContext.addEventListener(BUNDLE_UPDATED_EVENT, this._onBundleUpdated);
      }

      disconnectedCallback() {
        if (this.bundleContext && this._onBundleUpdated) {
          this.bundleContext.removeEventListener(BUNDLE_UPDATED_EVENT, this._onBundleUpdated);
        }
      }

      handleBundleEvent() {
        if (this.bundleContext.numBundleItems === 0) {
          startTransition(() => {
            this.querySelectorAll(".bundle-builder-summary-price--compare-at").forEach(
              (e) => (e.textContent = null)
            );
            this.querySelectorAll(".bundle-builder-summary-price--primary").forEach(
              (e) => (e.textContent = null)
            );
            this.querySelectorAll(".bundle-builder-summary-price--primary-one-time").forEach(
              (e) => (e.textContent = null)
            );
            this.updateButtonState();
          });
          return;
        }

        startTransition(() => {
          this.querySelectorAll(".bundle-builder-summary-price--compare-at").forEach((e) => {
            e.textContent = `$${this.bundleContext.totalOneTimePrice.toFixed(2)}`;
          });

          this.querySelectorAll(".bundle-builder-summary-price--primary-one-time").forEach((e) => {
            e.textContent = `$${this.bundleContext.totalOneTimeDiscountPrice.toFixed(2)}`;
          });

          this.querySelectorAll(".bundle-builder-summary-price--primary").forEach((e) => {
            e.textContent = `$${this.bundleContext.totalSubscriptionPrice.toFixed(2)}`;
          });

          const productNames = this.bundleContext.bundleItems.map((i) => i.title).join(" / ");
          this.querySelectorAll(".bundle-builder-delivery-modal-option-product-names").forEach(
            (e) => (e.textContent = productNames)
          );

          this.updateButtonState();
        });
      }

      updateButtonState() {
        if (this.bundleContext.numBundleItems >= this.bundleContext.minItems) {
          this.button.disabled = false;
          this.button.textContent = this.getAttribute("data-button-copy-continue");
        } else {
          this.button.disabled = true;
          this.button.textContent = this.getAttribute("data-button-copy");
        }
      }
    }
  );
}

if (!customElements.get("bundle-builder-summary-items")) {
  customElements.define(
    "bundle-builder-summary-items",
    class extends HTMLElement {
      connectedCallback() {
        const bundleContext = this.closest("bundle-builder-context");
        if (!bundleContext) {
          throw new Error(
            "Element `bundle-builder-summary-items` must be a child of `bundle-builder-context`"
          );
        }

        this.bundleContext = bundleContext;
        this._onBundleUpdated = () => this.handleBundleEvent();
        this.bundleContext.addEventListener(BUNDLE_UPDATED_EVENT, this._onBundleUpdated);
      }

      disconnectedCallback() {
        if (this.bundleContext && this._onBundleUpdated) {
          this.bundleContext.removeEventListener(BUNDLE_UPDATED_EVENT, this._onBundleUpdated);
        }
      }

      handleBundleEvent() {
        const existingList = this.querySelector("ul");
        if (!existingList || !BUNDLE_SUMMARY_ITEM || !EMPTY_BUNDLE_SUMMARY_ITEM || !BUNDLE_CTA_MOBILE) return;

        startTransition(() => {
          if (existingList.lastElementChild) {
            existingList.removeChild(existingList.lastElementChild);
          }

          let totalIndex = 0;

          this.bundleContext.bundleItems.forEach((item) => {
            for (let i = 0; i < item.quantity; i++) {
              const existingChild = existingList.children[totalIndex];

              if (existingChild) {
                if (existingChild.getAttribute("variant-id") !== String(item.variantId)) {
                  existingChild.setAttribute("variant-id", item.variantId);
                }
              } else {
                const element = BUNDLE_SUMMARY_ITEM.cloneNode(true).firstElementChild;
                element.setAttribute("variant-id", item.variantId);
                existingList.appendChild(element);
              }

              totalIndex++;
            }
          });

          while (existingList.children.length > totalIndex) {
            existingList.removeChild(existingList.lastElementChild);
          }

          let i = this.bundleContext.numBundleItems;
          while (i < this.bundleContext.minItems) {
            if (
              !existingList.children[i] ||
              !existingList.children[i].classList.contains("empty-item")
            ) {
              const emptyItem = EMPTY_BUNDLE_SUMMARY_ITEM.cloneNode(true);
              if (existingList.children[i]) {
                existingList.replaceChild(emptyItem, existingList.children[i]);
              } else {
                existingList.appendChild(emptyItem);
              }
            }
            i++;
          }

          if (this.bundleContext.numBundleItems < this.bundleContext.minItems) {
            existingList.appendChild(BUNDLE_CTA_MOBILE.cloneNode(true).firstElementChild);
          }
        });
      }
    }
  );
}

if (!customElements.get("bundle-builder-summary-item")) {
  customElements.define(
    "bundle-builder-summary-item",
    class extends HTMLElement {
      bundleContext;
      removeButton;

      static observedAttributes = ["variant-id"];

      connectedCallback() {
        const bundleContext = this.closest("bundle-builder-context");
        if (!bundleContext) {
          throw new Error(
            "Element `bundle-builder-product-card` must be a child of `bundle-builder-context`"
          );
        }
        this.bundleContext = bundleContext;
        if (this.variantId) this.render();
      }

      disconnectedCallback() {
        if (this.removeButton && this._onRemoveClick) {
          this.removeButton.removeEventListener("click", this._onRemoveClick);
        }
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === "variant-id" && oldValue !== newValue) {
          this.variantId = newValue ? parseInt(newValue, 10) : undefined;
          if (this.isConnected && this.bundleContext) this.render();
        }
      }

      render() {
        if (!EMPTY_BUNDLE_SUMMARY_ITEM || !BUNDLE_SUMMARY_ITEM) return;

        if (!this.variantId) {
          startTransition(() => {
            this.replaceWith(EMPTY_BUNDLE_SUMMARY_ITEM.cloneNode(true));
          });
          return;
        }

        const match = this.bundleContext.bundleItems.find((it) => it.variantId === this.variantId);
        if (!match) return;

        const newContent = BUNDLE_SUMMARY_ITEM.cloneNode(true).firstElementChild;

        const newImage = newContent.querySelector("img");
        if (newImage && newImage.src !== match.imageUrl) newImage.src = match.imageUrl;

        const newTitle = newContent.querySelector(".bundle-builder-summary-item--product-title");
        if (newTitle && newTitle.textContent !== match.title) newTitle.textContent = match.title;

        const newPrice = newContent.querySelector(".bundle-builder-summary-item-price--primary");
        if (newPrice) {
          const v = `$${match.subscriptionPrice.toFixed(2)}`;
          if (newPrice.textContent !== v) newPrice.textContent = v;
        }

        const newComparePrice = newContent.querySelector(".bundle-builder-summary-item-price--compare-at");
        if (newComparePrice) {
          const v = `$${match.oneTimePrice.toFixed(2)}`;
          if (newComparePrice.textContent !== v) newComparePrice.textContent = v;
        }

        startTransition(() => {
          this.replaceChildren(...newContent.childNodes);
          this.mountButton();
        });
      }

      mountButton() {
        if (this.removeButton && this._onRemoveClick) {
          this.removeButton.removeEventListener("click", this._onRemoveClick);
        }

        this.removeButton = this.querySelector(".bundle-builder-summary-item--remove-button");
        if (!this.removeButton) return;

        this._onRemoveClick = () => this.handleRemoveClick();
        this.removeButton.addEventListener("click", this._onRemoveClick);
      }

      handleRemoveClick() {
        const match = this.bundleContext.bundleItems.find((item) => item.variantId === this.variantId);
        if (!match) return;

        this.bundleContext.updateItem({
          variantId: this.variantId,
          quantity: match.quantity - 1,
        });
      }
    }
  );
}

if (!customElements.get("bundle-builder-delivery-modal")) {
  customElements.define(
    "bundle-builder-delivery-modal",
    class extends HTMLElement {
      connectedCallback() {
        this.bundleContext = this.closest("bundle-builder-context");
        if (!this.bundleContext) {
          throw new Error(
            "Element `bundle-builder-delivery-modal` must be a child of `bundle-builder-context`"
          );
        }

        this.selectedSellingPlanId =
          this.querySelector('input[name="subscription"]:checked')?.value || null;
        this.selectedOption = this.selectedSellingPlanId ? "subscription" : "one-time";

        this.addToCartButton = this.querySelector(".bundle-builder-add-to-cart");

        this._onOptionClick = (event) => this.handleDeliveryChange(event);
        this.querySelectorAll(".bundle-builder-delivery-modal-option").forEach((option) => {
          option.addEventListener("click", this._onOptionClick);
        });

        this._onCadenceChange = (event) => this.handleCadenceChange(event);
        this.querySelectorAll('input[name="subscription"]').forEach((input) => {
          input.addEventListener("change", this._onCadenceChange);
        });

        if (this.addToCartButton) {
          this._onAddToCart = () => this.handleAddToCart();
          this.addToCartButton.addEventListener("click", this._onAddToCart);
        }
      }

      disconnectedCallback() {
        if (this._onOptionClick) {
          this.querySelectorAll(".bundle-builder-delivery-modal-option").forEach((option) => {
            option.removeEventListener("click", this._onOptionClick);
          });
        }

        if (this._onCadenceChange) {
          this.querySelectorAll('input[name="subscription"]').forEach((input) => {
            input.removeEventListener("change", this._onCadenceChange);
          });
        }

        if (this.addToCartButton && this._onAddToCart) {
          this.addToCartButton.removeEventListener("click", this._onAddToCart);
        }
      }

      handleDeliveryChange(event) {
        const option = event.currentTarget;
        if (
          option.classList.contains("bundle-builder-delivery-modal-option--active")
        ) {
          return;
        }

        this.querySelectorAll(".bundle-builder-delivery-modal-option").forEach((opt) => {
          opt.classList.remove("bundle-builder-delivery-modal-option--active");
        });

        option.classList.add("bundle-builder-delivery-modal-option--active");

        this.selectedOption = option.querySelector('input[name="subscription"]')
          ? "subscription"
          : "one-time";

        this.selectedSellingPlanId =
          this.selectedOption === "subscription"
            ? option.querySelector('input[name="subscription"]:checked')?.value
            : null;

        this.updateButton();
      }

      handleCadenceChange(event) {
        this.selectedSellingPlanId = event.target.value;
        const el = this.querySelector(".bundle-builder-summary-cadence--selection");
        if (el) el.textContent = event.target.nextElementSibling?.textContent || "";
      }

      handleAddToCart() {
        const sellingPlanId =
          this.selectedOption === "subscription" ? this.selectedSellingPlanId : undefined;
        this.bundleContext.addToCart(sellingPlanId);
      }

      updateButton() {
        if (!this.addToCartButton) return;
        if (this.selectedOption === "subscription") {
          this.addToCartButton.textContent = this.getAttribute("data-subscribe-save-text");
        } else {
          this.addToCartButton.textContent = this.getAttribute("data-onetime-text");
        }
      }
    }
  );
}

function initSummaryVisibilityHandler() {
  function init() {
    const observedSection = document.querySelector(".shopify-section--image-with-text-overlay");
    const aside = document.querySelector("aside.bundle-builder-summary--wrapper");
    if (!aside) return;

    if (!observedSection) {
      aside.classList.remove("summary-hidden");
      return;
    }

    const rect = observedSection.getBoundingClientRect();
    const isAlreadyOutOfView =
      rect.bottom < 0 ||
      rect.right < 0 ||
      rect.left > window.innerWidth ||
      rect.top > window.innerHeight;

    if (isAlreadyOutOfView) {
      aside.classList.remove("summary-hidden");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) {
          aside.classList.remove("summary-hidden");
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "-10px" }
    );

    observer.observe(observedSection);

    setTimeout(() => {
      const newRect = observedSection.getBoundingClientRect();
      if (newRect.bottom < window.innerHeight * 0.7) {
        aside.classList.remove("summary-hidden");
        observer.disconnect();
      }
    }, 500);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}

initSummaryVisibilityHandler();
